import './DatePicker.css'

import { useEffect, useRef, useState } from 'react'

import { cap } from '../../../../State'
import { translations } from '../../../../translations'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Componente DatePicker minimal e pulito
 * @param {Object} props - Proprietà del componente
 * @param {Date} props.value - Data selezionata
 * @param {Function} props.onChange - Funzione chiamata quando cambia la data
 * @param {Function} props.onClear - Funzione chiamata per pulire la data
 * @param {string} props.placeholder - Testo placeholder
 * @param {string} props.id - ID univoco per il componente
 * @returns {JSX.Element} Componente DatePicker
 */
export function DatePicker({ value, onChange, onClear, placeholder = t.gfSelezionaData, id }) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null)
  const calendarRef = useRef(null)

  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value))
    }
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const formatDate = (date) => {
    if (!date) return ''
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setIsOpen(false)
    onChange && onChange(date)
  }

  const handleClear = () => {
    setSelectedDate(null)
    setIsOpen(false)
    // Chiama onClear se presente, altrimenti onChange con null
    if (onClear) {
      onClear()
    } else {
      onChange && onChange(null)
    }
  }

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const getCurrentMonthDays = () => {
    if (!selectedDate) {
      const today = new Date()
      return {
        days: getDaysInMonth(today),
        firstDay: getFirstDayOfMonth(today),
        month: today.getMonth(),
        year: today.getFullYear()
      }
    }
    return {
      days: getDaysInMonth(selectedDate),
      firstDay: getFirstDayOfMonth(selectedDate),
      month: selectedDate.getMonth(),
      year: selectedDate.getFullYear()
    }
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate || new Date())
    newDate.setMonth(newDate.getMonth() + direction)
    setSelectedDate(newDate)
  }

  const { days, firstDay, month, year } = getCurrentMonthDays()

  const monthNames = t.gfMonthNames
  const dayNames = t.gfDayNamesShort

  const hasValue = selectedDate !== null

  return (
    <div className="gigi-datepicker" ref={calendarRef}>
      <div className="gigi-datepicker-input" onClick={() => setIsOpen(!isOpen)} id={id}>
        <span className={selectedDate ? 'gigi-datepicker-value' : 'gigi-datepicker-placeholder'}>
          {selectedDate ? formatDate(selectedDate) : placeholder}
        </span>
        <div className="gigi-datepicker-actions">
          {hasValue && onClear && (
            <button
              type="button"
              className="gigi-datepicker-clear"
              onClick={(e) => {
                e.stopPropagation()
                handleClear()
              }}
              title={t.ggPulisci}
            >
              <i className="pi pi-times" />
            </button>
          )}
          <i className="pi pi-calendar gigi-datepicker-icon" />
        </div>
      </div>

      {isOpen && (
        <div className="gigi-datepicker-calendar">
          <div className="gigi-datepicker-header">
            <button
              type="button"
              className="gigi-datepicker-nav-button"
              onClick={() => navigateMonth(-1)}
            >
              <i className="pi pi-chevron-left" />
            </button>
            <div className="gigi-datepicker-month-year">
              {monthNames[month]} {year}
            </div>
            <button
              type="button"
              className="gigi-datepicker-nav-button"
              onClick={() => navigateMonth(1)}
            >
              <i className="pi pi-chevron-right" />
            </button>
          </div>

          <div className="gigi-datepicker-weekdays">
            {dayNames.map((day, index) => (
              <div key={index} className="gigi-datepicker-weekday">
                {day}
              </div>
            ))}
          </div>

          <div className="gigi-datepicker-days">
            {Array.from({ length: firstDay }).map((_, index) => (
              <div key={`empty-${index}`} className="gigi-datepicker-day-empty" />
            ))}
            {Array.from({ length: days }).map((_, index) => {
              const day = index + 1
              const dayDate = new Date(year, month, day)
              const isSelected =
                selectedDate &&
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === month &&
                selectedDate.getFullYear() === year
              const isToday = dayDate.toDateString() === new Date().toDateString()

              return (
                <button
                  key={day}
                  type="button"
                  className={`gigi-datepicker-day ${isSelected ? 'gigi-datepicker-day-selected' : ''} ${isToday ? 'gigi-datepicker-day-today' : ''}`}
                  onClick={() => handleDateSelect(dayDate)}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
